import type { ReactNode, MouseEvent } from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

/**
 * RwLink — the DS text link (rw-ds.css `.rw-link`).
 *
 * An inline text reference that **navigates** (real href), e.g. a record
 * name/identifier that opens its detail. For a standalone control use a Button;
 * for an embedded record chip use the boxed linked-record. Spec: rw-ds-components
 * § Link & Linked record.
 *
 * In-app navigation → pass `to` (react-router). Off-app → pass `href` + `external`
 * (adds `target=_blank` + `rel=noopener` and the trailing ↗ glyph). `sx` is layout-only.
 *
 * This repo shares `react-router` (not `react-router-dom`) as the Module Federation
 * singleton with the CRM host, and every route hook here imports from `react-router` —
 * `Link` is imported from the same package for consistency.
 */
export interface RwLinkProps {
  /** In-app route (react-router). */
  to?: string;
  /** Off-app URL. */
  href?: string;
  /** Off-app destination — adds target/rel + the ↗ glyph. */
  external?: boolean;
  onClick?: (e: MouseEvent) => void;
  className?: string;
  sx?: SxProps<Theme>;
  children?: ReactNode;
  'aria-label'?: string;
}

export default function RwLink({ to, href, external = false, onClick, className, sx, children, ...rest }: RwLinkProps) {
  const classes = ['rw-link', className].filter(Boolean).join(' ');
  const glyph = external ? <OpenInNewIcon sx={{ fontSize: 'var(--size-16)', ml: 0.5, verticalAlign: 'text-bottom' }} /> : null;

  if (to) {
    return (
      <Box component={RouterLink} to={to} className={classes} onClick={onClick} sx={sx} {...rest}>
        {children}
        {glyph}
      </Box>
    );
  }

  return (
    <Box
      component="a"
      href={href}
      className={classes}
      onClick={onClick}
      sx={sx}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      {...rest}
    >
      {children}
      {glyph}
    </Box>
  );
}
